/**
 * Cross-Tab Refresh Coordinator
 * WP-AUTH-REFRESH-DEDUP-1: Combines Layer 1 + Layer 2
 *
 * This coordinator ensures that:
 * 1. Within a single tab, concurrent refresh requests share one promise (Layer 1)
 * 2. Across multiple tabs, only one tab performs the refresh (Layer 2)
 *
 * The result is that N concurrent requests across M tabs = 1 actual /auth/refresh call.
 */

import { refreshWithDedup } from './refreshManager';
import {
  tryAcquireRefreshLock,
  releaseRefreshLock,
  publishRefreshResult,
  waitForRefreshResult,
} from '@/utils/broadcastChannel';

/**
 * Result from coordinated refresh.
 *
 * NOTE: When receiving result from another tab, we only get accessToken.
 * The refreshToken is already set via httpOnly cookie by the leader tab's response.
 * Callers should check `isLeaderResult` if they need to know if this was
 * a direct refresh or cross-tab coordination.
 */
export interface CoordinatedRefreshResult {
  accessToken: string;
  /** True if this tab performed the refresh, false if received from another tab */
  isLeaderResult: boolean;
}

/**
 * Perform token refresh with full coordination.
 *
 * 1. Try to acquire cross-tab lock
 * 2. If acquired: perform refresh, publish result
 * 3. If not acquired: wait for result from other tab
 *
 * NOTE: Cross-tab results only include accessToken. The refreshToken is
 * handled via httpOnly cookie, so follower tabs don't need it explicitly.
 *
 * IMPORTANT: Followers do NOT recursively retry on timeout. This prevents
 * infinite retry loops when the leader fails without publishing a result.
 * Instead, if timeout occurs, followers throw an error. This allows the
 * calling code (checkAuth) to handle the failure gracefully.
 */
export async function coordinatedRefresh(): Promise<CoordinatedRefreshResult> {
  const lockId = tryAcquireRefreshLock();

  if (lockId !== null) {
    // We are the leader - perform refresh
    try {
      const tokens = await refreshWithDedup();
      publishRefreshResult(tokens.accessToken);
      return { accessToken: tokens.accessToken, isLeaderResult: true };
    } finally {
      releaseRefreshLock(lockId);
    }
  } else {
    // Another tab is refreshing - wait for result
    const accessToken = await waitForRefreshResult();

    if (accessToken) {
      // Follower result - we only get accessToken, cookie is shared across tabs
      return { accessToken, isLeaderResult: false };
    }

    // Timeout waiting for result
    // Check if lock is still held (leader still working) vs released (leader failed)
    if (isAnyTabRefreshing()) {
      // Lock still held - leader is taking a long time, try one more wait
      console.debug('[Coordinator] Lock still held, waiting again');
      const retryToken = await waitForRefreshResult();
      if (retryToken) {
        return { accessToken: retryToken, isLeaderResult: false };
      }
    }

    // Either lock was released (leader failed) or second wait timed out
    // Don't recursively retry - throw error to let caller handle it
    // This prevents infinite retry loops when refresh fails (e.g., 401)
    console.debug('[Coordinator] Timeout, no result published - refresh likely failed');
    throw new Error('Token refresh timeout: leader did not publish result');
  }
}

/**
 * Check if any tab is currently refreshing.
 */
export function isAnyTabRefreshing(): boolean {
  const lock = localStorage.getItem('flowio_refresh_lock');
  if (!lock) return false;

  try {
    const parsed = JSON.parse(lock);
    return parsed.expiresAt > Date.now();
  } catch {
    return false;
  }
}
